import type {Mutate, MutationDef} from "../api-utils/types";
import type createCache from "./cache";
import type {ChangeListener, MutationDesc} from "./mate-types";
import {isPlaceholderId} from "./optimistic";

export type Dispatcher = (
  actionName: string,
  data: Record<string, unknown>,
  sendResult: (err: unknown, retVal?: any) => void
) => void;

type MutateFn = (data?: Record<string, any>, support?: Record<string, any>) => Promise<any>;

let nextMutationId = 1;

type Request = {
  mutationId: number;
  path: string;
  desc: MutationDesc;
  data: Record<string, any>;
  /** only the cache reads it; never sent */
  support: Record<string, any>;
  resolve: (retVal: unknown) => void;
  reject: (err: unknown) => void;
};

const isPlainObject = (val: unknown): val is Record<string, unknown> =>
  typeof val === "object" && val !== null && Object.getPrototypeOf(val) === Object.prototype;

export default function createMutator<M extends Record<string, MutationDef>>(
  mutations: M,
  dispatcher: Dispatcher,
  cache: ReturnType<typeof createCache>,
  registerOnChangeListener: ChangeListener,
  flushChanges: () => void
): Mutate<M> {
  // requests naming a row whose create hasn't been saved yet, in the order they were made
  const held: Request[] = [];

  /**
   * Swaps placeholder ids for the ids the server made. "pending" while one of them is still being
   * saved, "gone" when its create failed or made no row.
   */
  const withRealIds = <T>(value: T): "pending" | "gone" | {value: T} => {
    let result = null as "pending" | "gone" | null;
    const walk = (val: unknown): unknown => {
      if (isPlaceholderId(val)) {
        const state = cache.getPlaceholderState(val);
        if (state.state === "mapped") return state.id;
        if (state.state === "gone" || result === null) result = state.state;
        return val;
      }
      if (Array.isArray(val)) {
        const walked = val.map(walk);
        return walked.some((v, i) => v !== val[i]) ? walked : val;
      }
      if (isPlainObject(val)) {
        const entries = Object.entries(val);
        const walked = entries.map(([k, v]) => [k, walk(v)] as const);
        return walked.some(([, v], i) => v !== entries[i][1]) ? Object.fromEntries(walked) : val;
      }
      return val;
    };
    const next = walk(value) as T;
    return result ?? {value: next};
  };

  const send = (request: Request) => {
    const {mutationId, path, desc, data, support} = request;
    dispatcher(path, data, (err, retVal) => {
      if (err) {
        cache.failLayer(mutationId);
        request.reject(err);
      } else {
        cache.resolveLayer(mutationId, desc, {...data, ...support}, retVal, path);
        // resolve once the invalidated data has been handed to listeners, not in between
        const unsub = registerOnChangeListener(() => {
          unsub();
          request.resolve(retVal);
        });
      }
      releaseHeld();
    });
  };

  /** Sends out a request; null when it has to wait, false when it failed without being sent. */
  const trySend = (request: Request) => {
    const {data, support} = request;
    const res = withRealIds({data, support});
    if (res === "pending") return null;
    if (res === "gone") {
      cache.failLayer(request.mutationId);
      request.reject(new Error(`"${request.path}" refers to a row that could not be saved`));
      return false;
    }
    if (res.value.data !== data || res.value.support !== support) {
      request.data = res.value.data;
      request.support = res.value.support;
      cache.replaceLayerData(request.mutationId, request.desc, {
        ...request.data,
        ...request.support,
      });
    }
    send(request);
    return true;
  };

  const releaseHeld = () => {
    for (let i = 0; i < held.length; ) {
      if (trySend(held[i]) === null) i += 1;
      else held.splice(i, 1);
    }
  };

  const instance: Record<string, Record<string, MutateFn>> = {};
  for (const [fullActionName, mutationDescription] of Object.entries(mutations)) {
    const [actionNamespace, actionName] = fullActionName.split(".");
    const namespace = (instance[actionNamespace] ??= {});
    namespace[actionName] = (data = {}, support = {}) =>
      new Promise((resolve, reject) => {
        const mutationId = nextMutationId;
        nextMutationId += 1;
        const desc = mutationDescription as MutationDesc;
        cache.addLayer(mutationId, desc, {...data, ...support});
        // Listeners run now rather than on the next tick, so a change made in an event handler (a
        // drop, a click) renders before the browser paints the handler's other state changes.
        flushChanges();
        const request: Request = {
          mutationId,
          path: `${actionNamespace}/${actionName}`,
          desc,
          data,
          support,
          resolve,
          reject,
        };
        if (trySend(request) === null) held.push(request);
      });
  }

  // built key by key from `mutations`, which is what `Mutate<M>` spells out per entry
  return instance as Mutate<M>;
}
